// Élus et règles du conseil (M16, lot L5-B, D-222) : registre des mandats datés, versions des règles
// (quorum, majorité, vote, procurations, délai) avec leur source ; une règle n'est utilisable qu'une fois
// validée, puis figée. Aucune règle n'est fournie par l'application : à confirmer par l'administration.
import type { MandatElu, RegleConseil } from '@cc/contrats';
import { FONCTIONS_ELU } from '@cc/contrats';
import { Bandeau, Bouton, EtatVide, PuceEtat, Tableau, type ColonneTableau } from '@cc/ui';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { EnTetePage } from '../../commun/EnTetePage';
import { EtatRequete } from '../../commun/EtatRequete';
import { useAction } from '../../commun/useAction';
import { useSession } from '../../session/FournisseurSession';
import { peut } from '../../session/permissions';
import { INVALIDER_CONSEILS, useMandats, useRegles, validerRegle } from './api';
import { FormulaireMandat, FormulaireRegle } from './FormulairesConseils';
import { texteRegle } from './logique';
import { NavigationConseil } from './NavigationConseil';
import styles from '../achats/achats.module.css';

export function PageElus() {
  const { contexte } = useSession();
  const mandats = useMandats();
  const regles = useRegles();
  const [dialogue, setDialogue] = useState<'mandat' | 'regle' | null>(null);
  const validation = useAction({ executer: (id: string) => validerRegle(id), succes: 'Règle validée : elle est désormais figée.', invalider: INVALIDER_CONSEILS });
  const colonnes: ColonneTableau<MandatElu>[] = [
    { id: 'nom', entete: 'Élu', enTeteLigne: true, masquable: false, cellule: (m) => `${m.nom_complet}${m.fictif ? ' (fictif)' : ''}` },
    { id: 'fonction', entete: 'Fonction', cellule: (m) => FONCTIONS_ELU[m.fonction] },
    { id: 'groupe', entete: 'Groupe', cellule: (m) => m.groupe ?? '' },
    { id: 'periode', entete: 'Mandat', cellule: (m) => `du ${m.debut}${m.fin ? ` au ${m.fin}` : ''}` },
    { id: 'compte', entete: 'Compte', cellule: (m) => (m.compte ? 'Compte actif' : 'Sans compte') },
    { id: 'etat', entete: 'État', cellule: (m) => <PuceEtat ton={m.en_exercice ? 'ok' : 'neutre'} libelle={m.en_exercice ? 'En exercice' : 'Hors mandat'} /> },
  ];
  const listeRegles = regles.data?.elements ?? [];
  return (
    <>
      <EnTetePage
        etiquette="Conseil"
        titre="Élus et règles"
        description="Mandats datés des élus et règles de fonctionnement du conseil, saisies d'après les actes de la collectivité."
        actions={peut(contexte, 'assemblee', 'creer') ? <Bouton variante="principal" icone={Plus} onClick={() => setDialogue('mandat')}>Nouveau mandat</Bouton> : null}
      />
      <NavigationConseil actif="elus" />
      <EtatRequete chargement={mandats.isPending} erreur={mandats.error} onReessayer={() => void mandats.refetch()} lot="L5-B" fonction="Le registre des élus">
        <section className={styles.bloc} aria-labelledby="titre-elus">
          <h2 id="titre-elus">Élus et mandats</h2>
          {(mandats.data?.elements ?? []).length === 0 ? <EtatVide titre="Aucun élu"><p>Aucun mandat n&apos;est enregistré.</p></EtatVide> : <Tableau legende="Élus et mandats" colonnes={colonnes} lignes={mandats.data?.elements ?? []} cleLigne={(m) => m.id} cleColonnes="conseil-elus" />}
        </section>
      </EtatRequete>
      <section className={styles.bloc} aria-labelledby="titre-regles">
        <h2 id="titre-regles">Règles du conseil</h2>
        <Bandeau type="info">Quorum, majorité, mode de vote, procurations et délai de convocation sont des paramètres de la collectivité, à confirmer par l&apos;administration. L&apos;application n&apos;en fournit aucun ; sans règle validée, une session reste en préparation.</Bandeau>
        {peut(contexte, 'assemblee', 'modifier') ? <p><Bouton variante="secondaire" icone={Plus} onClick={() => setDialogue('regle')}>Nouvelle version des règles</Bouton></p> : null}
        {validation.messageErreur ? <p role="alert" className={styles.negatif}>{validation.messageErreur}</p> : null}
        {listeRegles.length === 0 ? <p className={styles.detail}>Aucune règle saisie : les sessions restent en préparation.</p> : (
          <ul className={styles.liste}>
            {listeRegles.map((r: RegleConseil) => (
              <li key={r.id}>
                <h3>{`Version ${r.version} : ${r.libelle}${r.fictif ? ' (fictive)' : ''}`}</h3>
                <p className={styles.detail}>Source : {r.reference_acte}</p>
                <ul>{texteRegle(r.parametres).map((t) => <li key={t}>{t}</li>)}</ul>
                <p className={styles.actesLigne}>
                  <PuceEtat ton={r.validee_le ? 'ok' : 'attente'} libelle={r.validee_le ? `Validée${r.validee_par ? ` par ${r.validee_par}` : ''}` : 'À valider'} />
                  {!r.validee_le && peut(contexte, 'assemblee', 'valider') ? <Bouton variante="secondaire" chargement={validation.enCours} onClick={() => validation.lancer(r.id)}>Valider la règle</Bouton> : null}
                </p>
              </li>
            ))}
          </ul>
        )}
      </section>
      {dialogue === 'mandat' ? <FormulaireMandat onFermer={() => setDialogue(null)} /> : null}
      {dialogue === 'regle' ? <FormulaireRegle onFermer={() => setDialogue(null)} /> : null}
    </>
  );
}
